import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { Portal } from '../components/Portal';
import { useTenant } from '../contexts/TenantContext';
import { useT } from '../i18n';

/**
 * Respostas automáticas: modelos prontos para restaurante (troca de número, fora do horário,
 * boas-vindas, menu, entrega, localização…) que a empresa escolhe, edita e liga por número.
 */

type Trigger = 'ANY' | 'FIRST_MESSAGE' | 'OUTSIDE_HOURS' | 'KEYWORDS';

interface Template {
  key: string; icon: string; name: string; trigger: Trigger; keywords?: string[]; openFrom?: string; openTo?: string;
  cooldownHours: number; delayMinutes?: number; priority: number; message: string; suggestMedia?: 'image' | 'document';
}

interface Rule {
  id?: string; name: string; templateKey: string | null; enabled: boolean; priority: number; trigger: Trigger;
  keywords: string[] | null; openFrom: string | null; openTo: string | null; sessionNames: string[] | null;
  message: string; mediaUrl: string | null; mediaType: string | null; fileName: string | null; cooldownHours: number; delayMinutes: number; sentCount?: number;
  lastError?: string | null; lastErrorAt?: string | null;
}

interface Session { name: string; displayName?: string; inboxEnabled?: boolean; interactiveCampaignEnabled?: boolean }

const COOLDOWNS = [0, 1, 6, 12, 24, 72, 168, 720];
const DELAYS = [0, 5, 10, 15, 30, 60];

interface CatchUp { id: string; status: 'RUNNING' | 'DONE' | 'STOPPED' | 'FAILED'; since: string; sent: number; skipped: number; failed: number; lastError: string | null }

// datetime-local: hoje às 09:00 no horário de quem está usando
function todayAtNine(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T09:00`;
}
const unfilled = (text: string) => [...text.matchAll(/\[([^\]\n]{1,60})\]/g)].map(m => m[1]);

function fromTemplate(tpl: Template | null): Rule {
  return {
    name: tpl?.name || '', templateKey: tpl?.key || null, enabled: false, priority: tpl?.priority ?? 100, trigger: tpl?.trigger || 'KEYWORDS',
    keywords: tpl?.keywords || [], openFrom: tpl?.openFrom || null, openTo: tpl?.openTo || null, sessionNames: null,
    message: tpl?.message || '', mediaUrl: null, mediaType: null, fileName: null, cooldownHours: tpl?.cooldownHours ?? 24,
    delayMinutes: tpl?.delayMinutes ?? 0,
  };
}

export function AutoRepliesPage() {
  const t = useT();
  const { selectedTenantId } = useTenant();
  const [rules, setRules] = useState<Rule[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [gallery, setGallery] = useState(false);
  const [editing, setEditing] = useState<Rule | null>(null);
  const [keywordText, setKeywordText] = useState('');
  const [busy, setBusy] = useState(false);
  const [catchUpSince, setCatchUpSince] = useState(todayAtNine());
  const [catchUpPreview, setCatchUpPreview] = useState<{ unanswered: number; matched: number; noRule: number } | null>(null);
  const [catchUp, setCatchUp] = useState<CatchUp | null>(null);

  const api = useCallback(async (url: string, options: RequestInit = {}) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` };
    if (selectedTenantId) headers['X-Tenant-Id'] = selectedTenantId;
    const response = await fetch(url, { ...options, headers });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
    return data;
  }, [selectedTenantId]);

  const load = useCallback(() => api('/api/auto-replies').then(setRules).catch(e => toast.error(e.message)), [api]);

  useEffect(() => {
    load();
    api('/api/auto-replies/templates').then(setTemplates).catch(() => undefined);
    api('/api/waha/sessions').then(setSessions).catch(() => undefined);
  }, [api, load]);

  const loadCatchUp = useCallback(() => api('/api/auto-replies/catch-up').then(setCatchUp).catch(() => undefined), [api]);
  useEffect(() => { loadCatchUp(); }, [loadCatchUp]);
  useEffect(() => {
    if (catchUp?.status !== 'RUNNING') return;
    const timer = setInterval(loadCatchUp, 5000);
    return () => clearInterval(timer);
  }, [catchUp?.status, loadCatchUp]);
  useEffect(() => { setCatchUpPreview(null); }, [catchUpSince]);

  const runCatchUp = async (path: string) => {
    setBusy(true);
    try {
      const since = new Date(catchUpSince).toISOString();
      if (path === 'preview') {
        setCatchUpPreview(await api('/api/auto-replies/catch-up/preview', { method: 'POST', body: JSON.stringify({ since }) }));
      } else if (path === 'start') {
        await api('/api/auto-replies/catch-up', { method: 'POST', body: JSON.stringify({ since }) });
        setCatchUpPreview(null);
        await loadCatchUp();
      } else if (catchUp) {
        await api(`/api/auto-replies/catch-up/${catchUp.id}/stop`, { method: 'POST' });
        await loadCatchUp();
      }
    } catch (error: any) {
      toast.error(error.message, { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  const iconOf = (rule: Rule) => templates.find(tpl => tpl.key === rule.templateKey)?.icon || '💬';
  const receiving = (s: Session) => s.inboxEnabled || s.interactiveCampaignEnabled;
  const silentNumbers = sessions.filter(s => !receiving(s));

  const openEditor = (rule: Rule) => {
    setEditing({ ...rule, keywords: rule.keywords || [] });
    setKeywordText((rule.keywords || []).join('، '));
    setGallery(false);
  };

  const save = async (rule: Rule) => {
    setBusy(true);
    try {
      const body = { ...rule, keywords: keywordText.split(/[,،\n]/).map(k => k.trim()).filter(Boolean) };
      if (rule.id) await api(`/api/auto-replies/${rule.id}`, { method: 'PUT', body: JSON.stringify(body) });
      else await api('/api/auto-replies', { method: 'POST', body: JSON.stringify(body) });
      toast.success(t('ar.saved'));
      setEditing(null);
      load();
    } catch (error: any) {
      toast.error(error.message, { duration: 8000 });
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (rule: Rule) => {
    try {
      await api(`/api/auto-replies/${rule.id}`, { method: 'PUT', body: JSON.stringify({ enabled: !rule.enabled }) });
      toast.success(rule.enabled ? t('ar.turnedOff') : t('ar.turnedOn'));
      load();
    } catch (error: any) {
      toast.error(error.message, { duration: 8000 });
    }
  };

  const remove = async (rule: Rule) => {
    if (!confirm(t('ar.confirmDelete', { title: rule.name }))) return;
    try {
      await api(`/api/auto-replies/${rule.id}`, { method: 'DELETE' });
      load();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const upload = async (file: File) => {
    if (!editing) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await fetch('/api/media/upload', { method: 'POST', body: form, headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` } });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.fileUrl) throw new Error(data.error || t('camp.templateUploadFailed'));
      const mime = String(data.mimetype || file.type);
      setEditing({ ...editing, mediaUrl: data.fileUrl, fileName: data.originalName || file.name, mediaType: mime.startsWith('image/') ? 'image' : mime.startsWith('video/') ? 'video' : 'document' });
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  const whenText = (rule: Rule) => {
    if (rule.trigger === 'KEYWORDS') return t('ar.when.KEYWORDS', { words: (rule.keywords || []).slice(0, 6).join('، ') });
    if (rule.trigger === 'OUTSIDE_HOURS') return t('ar.when.OUTSIDE_HOURS', { from: rule.openFrom || '', to: rule.openTo || '' });
    return t(`ar.when.${rule.trigger}`);
  };

  const editor = editing && (
    <Portal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto p-6 space-y-4">
          <h2 className="text-xl font-bold text-gray-900">{editing.id ? t('ar.editTitle') : t('ar.newTitle')}</h2>

          <label className="block">
            <span className="text-sm font-semibold text-gray-700">{t('ar.name')}</span>
            <input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md" />
          </label>

          <label className="block">
            <span className="text-sm font-semibold text-gray-700">{t('ar.trigger')}</span>
            <select value={editing.trigger} onChange={e => setEditing({ ...editing, trigger: e.target.value as Trigger })} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
              {(['ANY', 'FIRST_MESSAGE', 'OUTSIDE_HOURS', 'KEYWORDS'] as Trigger[]).map(tr => <option key={tr} value={tr}>{t(`ar.trigger.${tr}`)}</option>)}
            </select>
          </label>

          {editing.trigger === 'KEYWORDS' && (
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">{t('ar.keywords')}</span>
              <textarea dir="auto" rows={2} value={keywordText} onChange={e => setKeywordText(e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              <span className="text-xs text-gray-500">{t('ar.keywordsHelp')}</span>
            </label>
          )}

          {editing.trigger === 'OUTSIDE_HOURS' && (
            <div>
              <span className="text-sm font-semibold text-gray-700">{t('ar.openHours')}</span>
              <div className="flex items-center gap-2 mt-1">
                <input type="time" value={editing.openFrom || ''} onChange={e => setEditing({ ...editing, openFrom: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-md" />
                <span>→</span>
                <input type="time" value={editing.openTo || ''} onChange={e => setEditing({ ...editing, openTo: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-md" />
              </div>
              <span className="text-xs text-gray-500">{t('ar.openHoursHelp')}</span>
            </div>
          )}

          <label className="block">
            <span className="text-sm font-semibold text-gray-700">{t('ar.message')}</span>
            <textarea dir="auto" rows={7} value={editing.message} onChange={e => setEditing({ ...editing, message: e.target.value })} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
            <span className="text-xs text-gray-500">{t('ar.messageHelp')}</span>
          </label>
          {unfilled(editing.message).length > 0 && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">⚠ {t('ar.fillIn', { parts: unfilled(editing.message).join('، ') })}</p>
          )}

          <div>
            <span className="text-sm font-semibold text-gray-700">{t('ar.media')}</span>
            <div className="flex items-center gap-2 mt-1">
              <label className={`px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm cursor-pointer ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
                {editing.mediaUrl ? t('ar.mediaReplace') : t('ar.mediaUpload')}
                <input type="file" accept="image/*,application/pdf,video/mp4" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
              </label>
              {editing.mediaUrl && (
                <>
                  <span className="text-xs text-gray-600 truncate max-w-[12rem]" dir="ltr">{editing.fileName || editing.mediaType}</span>
                  <button type="button" onClick={() => setEditing({ ...editing, mediaUrl: null, mediaType: null, fileName: null })} className="text-xs text-red-600">{t('ar.mediaRemove')}</button>
                </>
              )}
            </div>
            {editing.mediaType === 'image' && editing.mediaUrl && <img src={editing.mediaUrl} alt="" className="mt-2 max-h-32 rounded border" />}
            <span className="text-xs text-gray-500">{t('ar.mediaHelp')}</span>
          </div>

          <div>
            <span className="text-sm font-semibold text-gray-700">{t('ar.numbers')}</span>
            <p className="text-xs text-gray-500">{t('ar.numbersHelp')}</p>
            <div className="mt-1 space-y-1">
              {sessions.map(s => (
                <label key={s.name} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={(editing.sessionNames || []).includes(s.name)}
                    onChange={e => {
                      const current = editing.sessionNames || [];
                      const next = e.target.checked ? [...current, s.name] : current.filter(n => n !== s.name);
                      setEditing({ ...editing, sessionNames: next.length ? next : null });
                    }} />
                  {s.displayName || s.name}
                  {!receiving(s) && <span className="text-xs text-amber-700">· {t('ar.notReceiving')}</span>}
                </label>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="text-sm font-semibold text-gray-700">{t('ar.delay')}</span>
            <select value={editing.delayMinutes} onChange={e => setEditing({ ...editing, delayMinutes: Number(e.target.value) })} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
              {(DELAYS.includes(editing.delayMinutes) ? DELAYS : [...DELAYS, editing.delayMinutes]).map(m => (
                <option key={m} value={m}>{m === 0 ? t('ar.delayNone') : t('ar.delayMinutes', { n: m })}</option>
              ))}
            </select>
            <span className="text-xs text-gray-500">{t('ar.delayHelp')}</span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">{t('ar.cooldown')}</span>
              <select value={editing.cooldownHours} onChange={e => setEditing({ ...editing, cooldownHours: Number(e.target.value) })} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md">
                {COOLDOWNS.map(h => <option key={h} value={h}>{t(`ar.cooldown.${h}`)}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">{t('ar.priority')}</span>
              <input type="number" min={0} max={1000} value={editing.priority} onChange={e => setEditing({ ...editing, priority: Number(e.target.value) })} className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md" />
              <span className="text-xs text-gray-500">{t('ar.priorityHelp')}</span>
            </label>
          </div>

          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={editing.enabled} onChange={e => setEditing({ ...editing, enabled: e.target.checked })} />
            {t('ar.enabled')}
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 bg-gray-100 rounded-md">{t('common.cancel')}</button>
            <button type="button" disabled={busy} onClick={() => save(editing)} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">{t('common.save')}</button>
          </div>
        </div>
      </div>
    </Portal>
  );

  const galleryModal = gallery && (
    <Portal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">{t('ar.galleryTitle')}</h2>
            <button onClick={() => setGallery(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {templates.map(tpl => (
              <button key={tpl.key} type="button" onClick={() => openEditor(fromTemplate(tpl))}
                className="text-start border border-gray-200 rounded-lg p-4 hover:border-blue-400 hover:shadow transition">
                <div className="font-semibold text-gray-900">{tpl.icon} {tpl.name}</div>
                <div className="text-xs text-blue-700 mt-1">
                  {whenText(fromTemplate(tpl))}
                  {tpl.delayMinutes ? ` · ${t('ar.afterMinutes', { n: tpl.delayMinutes })}` : ''}
                </div>
                <p dir="auto" className="text-xs text-gray-600 mt-2 whitespace-pre-line line-clamp-4">{tpl.message}</p>
              </button>
            ))}
            <button type="button" onClick={() => openEditor(fromTemplate(null))} className="text-start border border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400">
              <div className="font-semibold text-gray-900">✏️ {t('ar.blank')}</div>
              <div className="text-xs text-gray-500 mt-1">{t('ar.blankHelp')}</div>
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );

  return (
    <div>
      <Header title={t('ar.title')} subtitle={t('ar.subtitle')}
        actions={<button onClick={() => setGallery(true)} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm">+ {t('ar.add')}</button>} />
      <div className="p-6 space-y-4">
        {silentNumbers.length > 0 && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            ⚠ {t('ar.silentNumbers', { numbers: silentNumbers.map(s => s.displayName || s.name).join('، ') })}
          </p>
        )}
        <section className="bg-white rounded-lg shadow p-4 space-y-2">
          <h2 className="font-semibold text-gray-900">{t('ar.catchUpTitle')}</h2>
          <p className="text-sm text-gray-600">{t('ar.catchUpHelp')}</p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-700">{t('ar.catchUpSince')}</span>
            <input type="datetime-local" value={catchUpSince} onChange={e => setCatchUpSince(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-md text-sm" />
            <button disabled={busy || catchUp?.status === 'RUNNING'} onClick={() => runCatchUp('preview')} className="px-3 py-2 text-sm bg-gray-100 rounded-md disabled:opacity-50">{t('ar.catchUpCount')}</button>
            {catchUpPreview && catchUpPreview.matched > 0 && (
              <button disabled={busy} onClick={() => confirm(t('ar.catchUpConfirm', { n: catchUpPreview.matched })) && runCatchUp('start')} className="px-3 py-2 text-sm bg-green-600 text-white rounded-md disabled:opacity-50">{t('ar.catchUpStart', { n: catchUpPreview.matched })}</button>
            )}
          </div>
          {catchUpPreview && (
            <p className="text-sm text-blue-800 bg-blue-50 rounded px-3 py-2">{t('ar.catchUpPreview', { unanswered: catchUpPreview.unanswered, matched: catchUpPreview.matched, noRule: catchUpPreview.noRule })}</p>
          )}
          {catchUp && (
            <div className="text-sm flex flex-wrap items-center gap-2">
              <span className="font-medium">{t(`ar.catchUpStatus.${catchUp.status}`)}</span>
              <span className="text-gray-600">{t('ar.catchUpCounts', { sent: catchUp.sent, skipped: catchUp.skipped, failed: catchUp.failed })}</span>
              {catchUp.status === 'RUNNING' && <button disabled={busy} onClick={() => runCatchUp('stop')} className="px-2 py-1 text-xs bg-red-600 text-white rounded">{t('fu.stop')}</button>}
              {catchUp.lastError && <span dir="ltr" className="block w-full text-xs text-red-700 break-words">{catchUp.lastError}</span>}
            </div>
          )}
        </section>

        {rules.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            <p>{t('ar.empty')}</p>
            <button onClick={() => setGallery(true)} className="mt-3 px-4 py-2 bg-green-600 text-white rounded-lg text-sm">{t('ar.pickTemplate')}</button>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow divide-y">
            {rules.map(rule => (
              <div key={rule.id} className="p-4 flex flex-wrap items-start gap-3 justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{iconOf(rule)} {rule.name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${rule.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{rule.enabled ? t('ar.on') : t('ar.off')}</span>
                  </div>
                  <div className="text-xs text-blue-700 mt-1">
                    {whenText(rule)}
                    {rule.delayMinutes > 0 ? ` · ${t('ar.afterMinutes', { n: rule.delayMinutes })}` : ''}
                  </div>
                  <p dir="auto" className="text-sm text-gray-600 mt-1 whitespace-pre-line line-clamp-2">{rule.message}</p>
                  {rule.lastError && (
                    <p dir="ltr" className="text-xs text-red-700 bg-red-50 rounded px-2 py-1 mt-1 text-start break-words">
                      ⚠ {t('ar.lastError', { when: rule.lastErrorAt ? new Date(rule.lastErrorAt).toLocaleString() : '' })}: {rule.lastError}
                    </p>
                  )}
                  <div className="text-xs text-gray-400 mt-1">
                    {rule.sessionNames?.length ? rule.sessionNames.join('، ') : t('ar.allNumbers')} · {t(`ar.cooldown.${rule.cooldownHours}`)} · {t('ar.sentCount', { n: rule.sentCount || 0 })}
                    {rule.mediaUrl && ' · 📎'}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => toggle(rule)} className={`px-3 py-1 text-sm rounded text-white ${rule.enabled ? 'bg-gray-500' : 'bg-green-600'}`}>{rule.enabled ? t('ar.turnOff') : t('ar.turnOn')}</button>
                  <button onClick={() => openEditor(rule)} className="px-3 py-1 text-sm rounded bg-blue-600 text-white">{t('ar.edit')}</button>
                  <button onClick={() => remove(rule)} className="px-3 py-1 text-sm rounded bg-red-600 text-white">{t('ar.delete')}</button>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-500">{t('ar.rulesNote')}</p>
      </div>
      {galleryModal}
      {editor}
    </div>
  );
}
